import { useEffect, useState } from "react";
import Confetti from "react-confetti";
import Stepper from "../features/onboarding/stepper";
import { OnboardingForm } from "../features/onboarding/onboarding-form";
import { WelcomeSlide } from "../features/onboarding/welcome";
import FilesForm from "../features/onboarding/files-form";
import HowDidYouHearDemo from "../features/onboarding/how-did-you-hear-form";
import ProfileForm from "../features/onboarding/profile-form";
import { useUpdatePlan } from "@/hooks/useUpdatePlan";
import { LoadingAppLayout } from "@/ui/AppLayout";
import { useNavigate } from "react-router-dom";

const initialSteps = [
  {
    title: "Welcome",
    description: "Welcome to Samsearch! Let's get started.",
    status: "current",
  },
  {
    title: "Set Up Your Organization",
    description: "Provide details about your business to get started.",
    status: "upcoming",
  },
  {
    title: "Set Up Your Business Profile",
    description:
      "Update your profile and select your NAICS codes for accurate classification.",
    status: "upcoming",
  },
  {
    title: "Upload Your Documents",
    description:
      "Upload necessary documents to enhance the quality of your proposals.",
    status: "upcoming",
  },
  {
    title: "Finalize Your Setup",
    description:
      "Confirm your business details and finalize your account setup.",
    status: "upcoming",
  },
];

function PurchaseSuccess() {
  const [showConfetti, setShowConfetti] = useState(false);
  const { data, isSuccess, isLoading } = useUpdatePlan();
  const navigate = useNavigate();

  const [currentStepIndex, setCurrentStepIndex] = useState(0); // Starts at the first step (index 0)
  const [steps, setSteps] = useState(initialSteps);

  function handleStepComplete() {
    const updatedSteps = [...steps];
    updatedSteps[currentStepIndex] = {
      ...updatedSteps[currentStepIndex],
      status: "completed",
    };

    if (currentStepIndex + 1 < steps.length) {
      updatedSteps[currentStepIndex + 1] = {
        ...updatedSteps[currentStepIndex + 1],
        status: "current",
      };
    }

    setSteps(updatedSteps);
    setCurrentStepIndex((prevIndex) => prevIndex + 1);
  }

  function handleStepPrevious() {
    if (currentStepIndex > 0) {
      const updatedSteps = [...steps];
      updatedSteps[currentStepIndex] = {
        ...updatedSteps[currentStepIndex],
        status: "upcoming",
      };
      updatedSteps[currentStepIndex - 1] = {
        ...updatedSteps[currentStepIndex - 1],
        status: "current",
      };

      setSteps(updatedSteps);
      setCurrentStepIndex((prevIndex) => prevIndex - 1);
    }
  }

  useEffect(() => {
    if (isSuccess) {
      if (data) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 10000);

        if (window.gtag) {
          window.gtag("event", "conversion", {
            send_to: "AW-11501075405/aJD1CPWxu5oaEM33kewq",
          });
        }
      } else {
        navigate("/");
      }
    }
  }, [isSuccess, data]);

  if (isLoading) {
    return <LoadingAppLayout />;
  }

  const renderForm = () => {
    switch (currentStepIndex) {
      case 0: // Welcome Step
        return <WelcomeSlide onStepComplete={handleStepComplete} />;
      case 1:
        return (
          <OnboardingForm
            currentStepIndex={currentStepIndex}
            onStepComplete={handleStepComplete}
          />
        );
      case 2:
        return (
          <ProfileForm
            onStepComplete={handleStepComplete}
            onStepPrevious={handleStepPrevious}
          />
        );
      case 3:
        return (
          <FilesForm
            onStepComplete={handleStepComplete}
            onStepPrevious={handleStepPrevious}
          />
        );
      case 4:
        return (
          <HowDidYouHearDemo
            onStepComplete={handleStepComplete}
            onStepPrevious={handleStepPrevious}
          />
        );
      default:
        return (
          <OnboardingForm
            currentStepIndex={currentStepIndex}
            onStepComplete={handleStepComplete}
          />
        );
    }
  };

  return (
    <div className="h-screen w-screen flex justify-center items-center bg-blue-50 relative">
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          tweenDuration={10000}
          numberOfPieces={500}
        />
      )}

      <div className="flex flex-col lg:flex-row w-full h-full max-w-none bg-white">
        <div className="w-full lg:w-1/3 h-full flex flex-col justify-start lg:p-5 bg-blue-100 text-2xl text-gray-700 space-y-16">
          <div className="flex justify-center mt-4">
            <img src="logo.svg" alt="logo" width={90} height={90} />
          </div>

          <div className="flex">
            <h1 className="text-2xl lg:text-4xl font-extrabold">
              First Time Setup
            </h1>
          </div>

          <div className="mt-9">
            <Stepper steps={steps} />
          </div>
        </div>

        <main className="flex-1 h-full flex justify-center items-center p-6 lg:p-10 bg-white relative">
          {renderForm()}
        </main>
      </div>
    </div>
  );
}

export default PurchaseSuccess;
